import {
    CheckCircle2,
    Image as ImageIcon,
    Layers,
    Loader2,
    Minus,
    Plus,
    QrCode,
    Ticket,
    Wand2,
    X,
    ZoomIn
} from "lucide-react";
import React, {useEffect, useState} from "react";
import {generateSticker} from "../../api/ai/ai.api";
import {getSticker, listEntrypoint, uploadSticker} from "../../api/system/system.api";

/**
 * 스티커 '제작하지 않겠습니다' 를 나타내는 `account_channel.sticker_type` 값(2026-08-21).
 *
 * `null` 을 쓸 수 없다 — 서버 `updateConfig` 는 부분 업데이트라 null·빈문자열을
 * "이번 요청에 그 정보가 없다" 로 해석해 컬럼을 그대로 둔다(2026-08-20 데이터 유실 수정).
 * 그래서 '제작 안 함' 이라는 **의사 표시**를 명시적인 문자열로 저장한다.
 * `sticker_type` 은 varchar(10) 이라 4자는 문제없다.
 *
 * ⚠️ 이 값은 `stickerTypes` 목록에 넣지 않는다. 제작 가능한 종류가 아니기 때문이다 —
 * 서버 `AIGenerateService.generateSticker` 의 `when` 에도 해당 분기가 없다.
 */
export const STICKER_TYPE_NONE = 'none';

export const stickerTypes = [
    {
        id: 'hor_blue',
        name: '가로형 파란색',
        imageUrl: 'https://img.headless.co.kr/offline_qr_service/0ebd4197-e1d9-4075-860e-0e024a3c7618'
    },
    {
        id: 'hor_red',
        name: '가로형 빨간색',
        imageUrl: 'https://img.headless.co.kr/offline_qr_service/f915f6d6-a65c-4eed-be37-3ae243eb5089'
    },
    {
        id: 'hor_yellow',
        name: '가로형 노란색',
        imageUrl: 'https://img.headless.co.kr/offline_qr_service/b48a86db-668e-4493-8139-8a36ba9e0b77'
    },
    {
        id: 'ver_blue',
        name: '세로형 파란색',
        imageUrl: 'https://img.headless.co.kr/offline_qr_service/a226daa1-2f66-466e-8083-ccf0683f2d6c'
    },
    {
        id: 'ver_red',
        name: '세로형 빨간색',
        imageUrl: 'https://img.headless.co.kr/offline_qr_service/8d6d6f63-3704-4512-bdbe-d2002c7f98fc'
    },
    {
        id: 'ver_yellow',
        name: '세로형 노란색',
        imageUrl: 'https://img.headless.co.kr/offline_qr_service/972f5c7f-46f6-4abb-b13c-91337acd5efa'
    },
];

const StickerGeneratorForm = ({channelId, setGeneratedImage}) => {
    const [qrEndpoints, setQrEndpoints] = useState([])
    const [previewImage, setPreviewImage] = useState(null);
    const [stickerType, setStickerType] = useState('hor_blue');
    const [prizeCount, setPrizeCount] = useState(3);
    const [prizes, setPrizes] = useState([]);
    const [qrEndpoint, setQrEndpoint] = useState();

    const [isGenerating, setIsGenerating] = useState(false);
    const [imageUrl, setImageUrl] = useState(null)
    const [generateType, setGenerateType] = useState('hor_blue')
    const [initStickerType, setInitStickerType] = useState()

    const handlePrizeChange = (rank, value) => {
        setPrizes(prev => {
            prev[rank] = value;

            return prev
        });
    };

    const handleGenerate = () => {
        setIsGenerating(true);
        setImageUrl(null)
        setGeneratedImage(null);
        setGenerateType(stickerType)

        generateSticker(
            {
                type: stickerType,
                benefits: prizes,
                entryPointId: qrEndpoint
            }
        ).then(({data}) => {
            setIsGenerating(false);
            setImageUrl(data)
            setGeneratedImage(`data:image/png;base64,${data}`)
        }).catch((e) => {
            // 409(같은 채널 AI 생성 진행 중) · 503(OpenAI 실패) — 종전엔 처리되지 않은 rejection 으로 스피너가 영구히 남았다(2026-10-29).
            setIsGenerating(false);
            alert(e?.message || "AI 스티커 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.");
        })
    };

    const upload = () => {
        uploadSticker(channelId, generateType, imageUrl).then(() => {
            alert("업로드 되었습니다.")
        })
    }

    useEffect(() => {
        getSticker(channelId).then(({data}) => {
            if (data?.stickerType) {
                setInitStickerType(data.stickerType)
                // '제작하지 않겠습니다'(STICKER_TYPE_NONE)는 스티커 종류가 아니라 **의사 표시**다.
                // 그대로 선택해두면 제작 요청이 서버에서 실패한다(generateSticker 의 when 에 해당 분기가 없다).
                // 그래서 선택은 기본값으로 두고, 아래 안내 문구로 고객 의사만 알린다.
                if (data.stickerType !== STICKER_TYPE_NONE) {
                    setStickerType(data.stickerType)
                }
            }
            if (data?.stickerUrl) {
                setImageUrl(data.stickerUrl)
            }
        })

        listEntrypoint(channelId).then(({data}) => {
            setQrEndpoints(data.map(spot => ({id: spot.id, name: spot.name})))
        })
    }, [channelId]);

    return (
        <div className="gap-8">
            {/* 설정 패널 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold mb-6 border-b pb-4">제작 옵션 설정</h2>

                {/* 1. 스티커 종류 선택 (라디오 버튼 + 이미지 팝업) */}
                <div className="mb-8">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                        <Layers className="w-4 h-4" /> 스티커 종류
                    </label>

                    {/* 고객이 온보딩에서 '제작하지 않겠습니다' 를 선택한 경우. 제작 자체는 여전히 가능하다. */}
                    {initStickerType === STICKER_TYPE_NONE && (
                        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-[13px] text-amber-800 leading-relaxed">
                            고객이 온보딩에서 <strong>스티커를 제작하지 않겠다</strong>고 선택했습니다.
                            <br />
                            <span className="text-amber-700">필요하면 아래에서 종류를 골라 제작할 수 있습니다(고객 요청 확인 후 진행해 주세요).</span>
                        </div>
                    )}

                    <div className="grid grid-cols-3 gap-4">
                        {stickerTypes.map((type) => (
                            <div key={type.id} className="flex flex-col gap-3">
                                {/* 이미지 썸네일 (클릭 시 팝업) */}
                                <div
                                    onClick={() => setPreviewImage(type)}
                                    className="relative group w-full aspect-square rounded-xl overflow-hidden border border-gray-200 cursor-pointer shadow-sm hover:shadow-md transition-all"
                                >
                                    <img
                                        src={type.imageUrl}
                                        alt={type.name}
                                        className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                                    />

                                    {/* 돋보기 오버레이 */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" />
                                    </div>

                                    {/* 미리 선택됨 배지 (좌상단 배치) */}
                                    {initStickerType !== undefined && initStickerType === type.id && (
                                        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-green-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-md shadow-sm pointer-events-none">
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            <span className="text-xs font-semibold">미리 선택됨</span>
                                        </div>
                                    )}
                                </div>

                                {/* 라디오 버튼 선택 영역 */}
                                <label className="flex items-center justify-center gap-2 cursor-pointer group">
                                    <input
                                        type="radio"
                                        name="stickerType"
                                        value={type.id}
                                        checked={stickerType === type.id}
                                        onChange={() => setStickerType(type.id)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                                    />
                                    <span className={`text-sm font-medium transition-colors ${
                                        stickerType === type.id ? 'text-blue-700 font-bold' : 'text-gray-600 group-hover:text-gray-900'
                                    }`}>
                    {type.name}
                </span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 2. 쿠폰 설정 */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <Ticket className="w-4 h-4" /> 당첨 쿠폰 설정
                        </label>
                        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setPrizeCount(Math.max(1, prizeCount - 1))}
                                className="p-1 hover:bg-white rounded text-gray-600 shadow-sm transition-colors"
                                disabled={prizeCount <= 1}
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <span className="text-sm font-bold w-4 text-center">{prizeCount}</span>
                            <button
                                onClick={() => setPrizeCount(Math.min(5, prizeCount + 1))}
                                className="p-1 hover:bg-white rounded text-gray-600 shadow-sm transition-colors"
                                disabled={prizeCount >= 5}
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="space-y-3 bg-gray-50 p-4 rounded-xl">
                        {Array.from({ length: prizeCount }).map((_, index) => (
                            <div key={index} className="flex items-center gap-3">
                                <span className="text-sm font-bold w-8 text-blue-600">{index + 1}등</span>
                                <input
                                    type="text"
                                    placeholder={`${index + 1}등 쿠폰 내용을 입력하세요`}
                                    value={prizes[index]}
                                    onChange={(e) => handlePrizeChange(index, e.target.value)}
                                    className="flex-1 text-sm p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* 3. 진입점(QR) 설정 */}
                <div className="mb-8">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                        <QrCode className="w-4 h-4" /> QR코드 진입점
                    </label>
                    <select
                        value={qrEndpoint}
                        onChange={(e) => setQrEndpoint(e.target.value)}
                        className="w-full text-sm p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                        <option value={null} disabled selected>선택해 주세요</option>
                        {qrEndpoints.map(ep => (
                            <option key={ep.id} value={ep.id}>{ep.name}</option>
                        ))}
                    </select>
                </div>

                {/* 생성 버튼 */}
                <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !qrEndpoint}
                    className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white font-bold py-4 px-6 rounded-xl hover:bg-gray-800 transition-colors disabled:bg-gray-400"
                >
                    {isGenerating ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            AI가 스티커를 생성하는 중...
                        </>
                    ) : (
                        <>
                            <Wand2 className="w-5 h-5" />
                            AI 스티커 생성하기
                        </>
                    )}
                </button>
                {imageUrl && (
                    <button
                        onClick={upload}
                        disabled={isGenerating}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-4 px-6 rounded-xl hover:bg-blue-500 transition-colors disabled:bg-gray-400 mt-4"
                    >
                        업로드
                    </button>
                )}
            </div>

            {/* 이미지 크게 보기 팝업 (모달) */}
            {previewImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
                    onClick={() => setPreviewImage(null)} // 배경 클릭 시 닫기
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-2xl w-full flex flex-col animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()} // 모달 내부 클릭 시 닫히지 않도록 방지
                    >
                        {/* 팝업 헤더 */}
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <ImageIcon className="w-5 h-5 text-gray-500" />
                                {previewImage.name} 예시
                            </h3>
                            <button
                                onClick={() => setPreviewImage(null)}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* 팝업 콘텐츠 (큰 이미지) */}
                        <div className="p-4 bg-gray-50 flex justify-center items-center h-[50vh] min-h-[400px]">
                            <img
                                src={previewImage.imageUrl}
                                alt={previewImage.name}
                                className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default StickerGeneratorForm